"use client";

import { Search } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Country } from "react-phone-number-input";
import { getCountryCallingCode } from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

interface CountryOption {
  value?: Country;
  label?: string;
}

interface CountrySelectProps {
  value?: Country;
  onChange: (value?: Country) => void;
  options: CountryOption[];
  disabled?: boolean;
  [key: string]: unknown;
}

// Real SVG flags (render correctly on every OS, unlike emoji flags on Windows).
const FLAGS = flags as Record<string, React.FC<{ title?: string }> | undefined>;

function Flag({ country }: { country?: Country }) {
  const Svg = country ? FLAGS[country] : undefined;
  return (
    <span className="flex h-4 w-[26px] shrink-0 items-center justify-center overflow-hidden rounded-[3px] shadow-[0_0_0_1px_rgba(0,0,0,0.06)] [&_svg]:h-full [&_svg]:w-full [&_svg]:object-cover">
      {Svg ? <Svg title={country} /> : <span className="text-[10px] font-bold text-ink-500">🌐</span>}
    </span>
  );
}

const DROPDOWN_WIDTH = 320;

export function CountrySelect({ value, onChange, options, disabled }: CountrySelectProps) {
  const t = useTranslations("auth");
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let left = r.left;
    if (left + DROPDOWN_WIDTH > window.innerWidth - 8)
      left = window.innerWidth - 8 - DROPDOWN_WIDTH;
    if (left < 8) left = 8;
    setPos({ top: r.bottom + 8, left });
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setSearch("");
  }, []);

  // Position on open + keep in sync with scroll/resize
  useLayoutEffect(() => {
    if (!isOpen) return;
    place();
    const onScrollResize = () => place();
    window.addEventListener("resize", onScrollResize);
    window.addEventListener("scroll", onScrollResize, true);
    return () => {
      window.removeEventListener("resize", onScrollResize);
      window.removeEventListener("scroll", onScrollResize, true);
    };
  }, [isOpen, place]);

  // Focus search + scroll selected into view
  useEffect(() => {
    if (!isOpen) return;
    const id = setTimeout(() => {
      searchRef.current?.focus();
      listRef.current
        ?.querySelector("[data-selected='true']")
        ?.scrollIntoView({ block: "center" });
    }, 40);
    return () => clearTimeout(id);
  }, [isOpen]);

  // Close on outside click (trigger + portal panel both count as inside) and Esc
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, close]);

  const validOptions = options.filter((o) => o.value);
  const filtered = validOptions.filter((opt) => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (
      opt.label?.toLowerCase().includes(q) ||
      getCountryCallingCode(opt.value!).includes(q.replace("+", ""))
    );
  });

  const callingCode = value ? `+${getCountryCallingCode(value)}` : "";

  return (
    <>
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        className="flex h-full items-center gap-2 py-1 pe-2 outline-none transition-opacity disabled:opacity-50"
      >
        <Flag country={value} />
        <span dir="ltr" className="text-sm font-semibold text-ink-900">
          {callingCode}
        </span>
        <svg
          className={`size-3 shrink-0 text-ink-500 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        >
          <path d="M2 4l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Dropdown — rendered in a portal so the modal's overflow never clips it */}
      {isOpen &&
        pos &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={panelRef}
            dir="rtl"
            // Above every dialog (login z-10051, booking z-10010) — it opens from inside them.
            style={{ top: pos.top, left: pos.left, width: DROPDOWN_WIDTH }}
            className="fixed z-[20000] overflow-hidden rounded-2xl border border-clay-100 bg-surface shadow-[0_20px_50px_-12px_rgba(28,25,23,0.35)] dark:border-zinc-700 dark:bg-zinc-900"
          >
            {/* Search */}
            <div className="border-b border-clay-100 p-2 dark:border-zinc-800">
              <div className="flex items-center gap-2 rounded-xl bg-clay-50 px-3 py-2.5 dark:bg-zinc-800">
                <Search size={15} className="shrink-0 text-ink-500" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t("searchCountry")}
                  className="w-full bg-transparent text-sm text-ink-900 outline-none placeholder:text-ink-500/60 dark:text-white"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="shrink-0 text-lg leading-none text-ink-500 hover:text-ink-900"
                    aria-label="clear"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* List */}
            <div ref={listRef} role="listbox" className="max-h-72 overflow-y-auto overscroll-contain p-1.5">
              {filtered.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-ink-500">
                  {t("noCountriesFound")}
                </div>
              ) : (
                filtered.map((opt) => {
                  const isSelected = opt.value === value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      data-selected={isSelected}
                      aria-selected={isSelected}
                      onClick={() => {
                        onChange(opt.value);
                        close();
                      }}
                      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-start text-sm transition-colors duration-100 ${
                        isSelected
                          ? "bg-clay-600 font-bold text-white"
                          : "text-ink-900 hover:bg-clay-50"
                      }`}
                    >
                      <Flag country={opt.value} />
                      <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                      <span
                        dir="ltr"
                        className={`shrink-0 text-xs font-medium ${isSelected ? "text-white/80" : "text-ink-500"}`}
                      >
                        +{getCountryCallingCode(opt.value!)}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
